import { PlayIcon, SourcesIcon, WaitIcon } from '../../../shared/ui/icons';
import { ClippedText } from '../../../shared/ui/clipped-text';
import { Time } from '../../../shared/ui/time';
import type { IColumn } from '../../../shared/ui/table';
import type { TFlow } from '../../../entities/flow';
import { FolderPath } from '../../../entities/folder';
import { HealthGlyph } from './health-glyph';

const TRIGGER_ICON = { webhook: SourcesIcon, schedule: WaitIcon, manual: PlayIcon } as const;

const triggerLabel = (trigger: TFlow['trigger']): string => {
  if (trigger.kind === 'webhook') return trigger.sourceName ?? 'Deleted source';
  if (trigger.kind === 'schedule') return trigger.text ?? 'Schedule';
  return 'Manual';
};

const StartsFrom = ({ trigger }: { trigger: TFlow['trigger'] }) => {
  // A new flow's stored kind is a default, not a choice anyone made.
  if (!trigger.chosen) return <span className="text-meta text-muted">Not chosen yet</span>;
  const Icon = TRIGGER_ICON[trigger.kind];
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Icon size={11} />
      <ClippedText text={triggerLabel(trigger)} lines={2} />
    </span>
  );
};

// Not `state !== 'active'` alone: the mock also breaks out draft-only flows, which are
// still technically "active" but have never published anything a run could use.
const State = ({ flow }: { flow: TFlow }) => {
  if (!flow.liveVersion) {
    return <span className="text-meta text-muted">draft, never published</span>;
  }
  if (flow.state !== 'active') {
    return <span className="text-meta text-muted">{flow.state}</span>;
  }
  return (
    <span className="flex flex-wrap items-center gap-x-2">
      <span className="text-meta text-primary">live v{flow.liveVersion.number}</span>
      {flow.draft.edited && (
        <span className="font-mono text-micro text-muted">
          · draft
          {flow.draftChangeCount > 0 &&
            ` · ${flow.draftChangeCount.toLocaleString('en-US')} ${
              flow.draftChangeCount === 1 ? 'change' : 'changes'
            }`}
        </span>
      )}
    </span>
  );
};

// `last24h` is real either way; the mock deliberately reads these as "not applicable"
// rather than "zero traffic" once nothing can run — disabled or never published.
const notApplicable = (flow: TFlow): boolean => !flow.liveVersion || flow.state === 'disabled';

const Num = ({ flow, value }: { flow: TFlow; value: number }) =>
  notApplicable(flow) ? (
    <span className="text-faint">—</span>
  ) : (
    <>{value.toLocaleString('en-US')}</>
  );

const Failed = ({ flow }: { flow: TFlow }) => {
  if (notApplicable(flow)) return <span className="text-faint">—</span>;
  const value = flow.last24h.failed;
  return (
    <span className={value > 0 ? 'text-danger-text' : undefined}>
      {value.toLocaleString('en-US')}
    </span>
  );
};

const InProgress = ({ flow }: { flow: TFlow }) => {
  if (notApplicable(flow)) return <span className="text-faint">—</span>;
  const running = flow.last24h.running;
  const older = flow.waitingByVersion.filter((entry) => entry.number !== flow.liveVersion?.number);
  const olderOpen = older.reduce((sum, entry) => sum + entry.open, 0);

  if (flow.state === 'paused' && running > 0) return <>{running.toLocaleString('en-US')} waiting</>;
  if (olderOpen > 0) {
    const numbers = older.flatMap((entry) => (entry.number === null ? [] : [entry.number]));
    const onWhich =
      numbers.length === 0
        ? 'older versions'
        : numbers.length === 1
          ? `v${numbers[0]}`
          : `v${Math.min(...numbers)}–v${Math.max(...numbers)}`;
    // Two lines, never a cut: "8 · 8 on older versions" on one line ran past the column.
    return (
      <span className="flex flex-col items-end">
        <span>{running.toLocaleString('en-US')}</span>
        <span
          className="font-mono text-micro whitespace-nowrap text-muted"
          title="Still running on versions before the live one"
        >
          {olderOpen.toLocaleString('en-US')} on {onWhich}
        </span>
      </span>
    );
  }
  return <>{running.toLocaleString('en-US')}</>;
};

const LastRun = ({ at, now, timeZone }: { at: string | null; now: number; timeZone?: string }) =>
  at ? <Time iso={at} now={now} timeZone={timeZone} /> : <span className="text-faint">never</span>;

export const flowColumns = (
  now: number,
  timeZone?: string,
  searching = false,
): IColumn<TFlow>[] => [
  {
    key: 'health',
    header: '',
    width: 'w-col-flow-icon',
    ui: true,
    render: (flow) => <HealthGlyph health={flow.health} />,
  },
  {
    key: 'name',
    header: 'Flow',
    // Takes what the fixed columns leave, and wraps to a second line before it cuts (00-rules).
    width: 'grow',
    ui: true,
    render: (flow) =>
      searching ? (
        <span className="flex min-w-0 flex-col">
          <ClippedText text={flow.name} />
          <FolderPath scope="flows" path={flow.folderPath} />
        </span>
      ) : (
        <ClippedText text={flow.name} lines={2} />
      ),
  },
  {
    key: 'startsFrom',
    header: 'Starts from',
    width: 'w-col-flow-start',
    ui: true,
    render: (flow) => <StartsFrom trigger={flow.trigger} />,
  },
  {
    key: 'state',
    header: 'State',
    width: 'w-col-flow-live',
    ui: true,
    render: (flow) => <State flow={flow} />,
  },
  {
    key: 'runs',
    header: 'Runs',
    width: 'w-col-flow-count',
    align: 'right',
    render: (flow) => <Num flow={flow} value={flow.last24h.runs} />,
  },
  {
    key: 'done',
    header: 'Done',
    width: 'w-col-flow-count',
    align: 'right',
    render: (flow) => <Num flow={flow} value={flow.last24h.completed} />,
  },
  {
    key: 'stopped',
    header: 'Stopped',
    width: 'w-col-flow-count',
    align: 'right',
    hint: 'A Filter or Dedupe ended the run on purpose.',
    render: (flow) => <Num flow={flow} value={flow.last24h.stopped} />,
  },
  {
    key: 'failed',
    header: 'Failed',
    width: 'w-col-flow-failed',
    align: 'right',
    render: (flow) => <Failed flow={flow} />,
  },
  {
    key: 'progress',
    header: 'In progress',
    width: 'w-col-flow-progress',
    align: 'right',
    render: (flow) => <InProgress flow={flow} />,
  },
  {
    key: 'lastRun',
    header: 'Last run',
    width: 'w-col-flow-last',
    align: 'right',
    render: (flow) => <LastRun at={flow.lastRunAt} now={now} timeZone={timeZone} />,
  },
];
